/**
 * constants/accountType.ts
 * Account type display data and options
 *
 * Provides UI-ready account type options with labels, descriptions, and icons.
 * Used for account type selection in onboarding and account management flows.
 */

// Internal absolute imports
import type { AccountTypeOption } from '@/types/shared/AccountType';

/**
 * Standard account type options for UI components
 */
export const ACCOUNT_TYPE_OPTIONS = [
  {
    value: 'personal',
    label: 'Personal Trading Account',
    description: 'Your own funded account with personal capital',
    icon: 'User'
  },
  {
    value: 'retirement',
    label: 'Retirement Account',
    description: 'IRA, 401(k), or other retirement investment account',
    icon: 'PiggyBank'
  },
  {
    value: 'business',
    label: 'Business/Corporate Account',
    description: 'Trading account for a business entity or corporation',
    icon: 'Briefcase'
  },
  {
    value: 'trust',
    label: 'Trust Account',
    description: 'Account managed on behalf of a trust',
    icon: 'Shield'
  },
  {
    value: 'pooled',
    label: 'Pooled Investment Fund',
    description: 'Shared investment vehicle or fund account',
    icon: 'Users'
  },
  {
    value: 'paper',
    label: 'Paper Trading Account',
    description: 'Simulated trading account for practice and testing',
    icon: 'FileText'
  },
  {
    value: 'prop',
    label: 'Prop Firm Account',
    description: 'Trading account through a proprietary trading firm',
    icon: 'Building'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Custom account type not listed above',
    icon: 'Settings'
  }
] as const satisfies readonly AccountTypeOption[];
